/**
 * `RenewContractDialog` — bouton "Renouveler" affiché sur un contrat
 * SIGNÉ qui approche de sa date de fin. L'admin choisit les nouvelles
 * dates ; tous les autres paramètres (rôle, région, type, salaire) sont
 * hérités du contrat parent.
 *
 * Le renouvellement :
 *   - crée un NOUVEAU contrat en status "draft" puis dispatched vers
 *     status "sent" (email au candidat)
 *   - marque `is_renewal=true` + `parent_contract_id` pour le suivi
 *   - CEO 2026-11-01 : période d'essai choisie EXPLICITEMENT à chaque
 *     renouvellement (avec N jours, modifiable / sans) — aucun choix imposé.
 */
import { CalendarPlus, Loader2, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { computeMaxTrialDays } from "../lib/trial";
import { Button } from "./ui/button";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";


function addDaysIso(iso, days) {
    if (!iso) return "";
    const d = new Date(iso);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}

function addMonthsIso(iso, months) {
    if (!iso) return "";
    const d = new Date(iso);
    d.setUTCMonth(d.getUTCMonth() + months);
    return d.toISOString().slice(0, 10);
}

const frDate = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "?");


export function RenewContractDialog({ contract, onRenewed }) {
    const [open, setOpen] = useState(false);
    // Default new start = day after parent's end_date
    const parentEnd = contract?.params?.end_date || "";
    const defaultStart = useMemo(() => addDaysIso(parentEnd, 1), [parentEnd]);
    const defaultEnd = useMemo(() => addMonthsIso(defaultStart, 1), [defaultStart]);

    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);
    const [busy, setBusy] = useState(false);
    // null = pas encore choisi (obligatoire) · "with" · "without"
    const [trialMode, setTrialMode] = useState(null);
    const [trialDays, setTrialDays] = useState("");
    const legalMax = computeMaxTrialDays(startDate, endDate);

    async function submit() {
        if (!startDate || !endDate) {
            toast.error("Renseignez les 2 nouvelles dates");
            return;
        }
        if (endDate <= startDate) {
            toast.error("La date de fin doit être postérieure à la date d'entrée");
            return;
        }
        if (!trialMode) {
            toast.error("Choisissez avec ou sans période d'essai");
            return;
        }
        const days = trialMode === "with" ? parseInt(trialDays || legalMax, 10) : 0;
        if (trialMode === "with" && (!days || days < 1)) {
            toast.error("Indiquez la durée de la période d'essai (en jours)");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post(`/contracts/${contract.id}/renew`, {
                start_date: startDate,
                end_date: endDate,
                trial_days: days,
            });
            toast.success("Renouvellement envoyé au candidat pour signature.");
            onRenewed?.(data);
            setOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Renouvellement impossible");
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    data-testid="contract-renew-btn"
                    title="Renouveler le contrat avec de nouvelles dates"
                    className="border-shariti-orange text-shariti-orange hover:bg-shariti-orange-soft"
                >
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                    Renouveler
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" data-testid="renew-contract-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <CalendarPlus className="h-5 w-5 text-shariti-orange" />
                        Renouveler le contrat
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <p className="text-sm text-slate-600 leading-relaxed">
                        Un nouveau contrat sera généré à partir des mêmes paramètres
                        (rôle, région, salaire) — seules les dates changent. Le candidat reçoit
                        automatiquement le lien de signature.
                    </p>
                    <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-600 leading-snug">
                        <strong>Contrat actuel :</strong> du {parentEnd ? frDate(contract.params.start_date) : "?"} au {frDate(parentEnd)}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="renew-start">Nouvelle date d&apos;entrée</Label>
                            <Input
                                id="renew-start"
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                data-testid="renew-start-date"
                            />
                        </div>
                        <div>
                            <Label htmlFor="renew-end">Nouvelle date de fin</Label>
                            <Input
                                id="renew-end"
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                data-testid="renew-end-date"
                            />
                        </div>
                    </div>
                    <TrialChoice
                        mode={trialMode}
                        setMode={setTrialMode}
                        days={trialDays}
                        setDays={setTrialDays}
                        legalMax={legalMax}
                    />
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
                    <Button
                        onClick={submit}
                        disabled={busy || !trialMode}
                        data-testid="renew-contract-confirm"
                        className="bg-shariti-orange hover:bg-shariti-orange/90"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        {busy ? "Génération…" : "Générer et envoyer"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Choix obligatoire à chaque renouvellement : avec (durée modifiable) ou sans période d'essai.
function TrialChoice({ mode, setMode, days, setDays, legalMax }) {
    const pill = (active) => `flex-1 h-10 rounded-full text-xs font-extrabold border transition ${active ? "bg-shariti-orange text-white border-shariti-orange" : "bg-white text-slate-600 border-slate-200 hover:border-shariti-orange"}`;
    return (
        <div className="space-y-2" data-testid="renew-trial-choice">
            <Label>Période d&apos;essai — à choisir</Label>
            <div className="flex gap-2">
                <button type="button" className={pill(mode === "with")} data-testid="renew-trial-with"
                    onClick={() => { setMode("with"); if (!days) setDays(String(legalMax || "")); }}>
                    Avec période d&apos;essai
                </button>
                <button type="button" className={pill(mode === "without")} data-testid="renew-trial-without"
                    onClick={() => setMode("without")}>
                    Sans période d&apos;essai
                </button>
            </div>
            {mode === "with" && (
                <div className="flex items-center gap-2">
                    <Input type="number" min={1} max={30} value={days} onChange={(e) => setDays(e.target.value)}
                        className="w-24" data-testid="renew-trial-days" />
                    <span className="text-xs text-slate-500">jours · maximum légal pour ces dates : {legalMax} j</span>
                </div>
            )}
            {mode === "without" && (
                <p className="text-[11px] text-slate-500">Le contrat indiquera « Aucune période d&apos;essai ».</p>
            )}
        </div>
    );
}
